import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Category } from '../types/category';
import { useCategories } from './useCategories';

export function useCategoryMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createCategory, updateCategory, deleteCategory, refetch } = useCategories();

  const handleCreate = useCallback(async (categoryData: Omit<Category, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    setLoading(true);
    setError(null);

    try {
      const newCategory = await createCategory(categoryData);
      toast.success('Categoria criada com sucesso!');
      return { data: newCategory, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar categoria';
      setError(errorMessage);
      toast.error(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [createCategory]);

  const handleUpdate = useCallback(async (id: string, updates: Partial<Category>) => {
    setLoading(true);
    setError(null);

    try {
      const updatedCategory = await updateCategory(id, updates);
      toast.success('Categoria atualizada com sucesso!');
      return { data: updatedCategory, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar categoria';
      setError(errorMessage);
      toast.error(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [updateCategory]);

  const handleDelete = useCallback(async (id: string, name: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteCategory(id);
      toast.success(`Categoria "${name}" excluída com sucesso!`);
      return { success: true, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir categoria';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [deleteCategory]);

  const optimisticCreate = useCallback(async (categoryData: Omit<Category, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    // Create optimistic category with temporary ID
    const optimisticCategory: Category = {
      ...categoryData,
      id: `temp-${Date.now()}`,
      user_id: 'current-user',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      // Optimistically add to state (will be handled by real-time subscription)
      const result = await handleCreate(categoryData);
      return result;
    } catch (err) {
      // If it fails, the real-time subscription won't add it, so no rollback needed
      return { data: null, error: err instanceof Error ? err.message : 'Erro ao criar categoria' };
    }
  }, [handleCreate]);

  const optimisticUpdate = useCallback(async (id: string, updates: Partial<Category>, originalCategory: Category) => {
    try {
      // Optimistically update (will be handled by real-time subscription)
      const result = await handleUpdate(id, updates);
      return result;
    } catch (err) {
      // If it fails, the real-time subscription won't update, so no rollback needed
      return { data: null, error: err instanceof Error ? err.message : 'Erro ao atualizar categoria' };
    }
  }, [handleUpdate]);

  const optimisticDelete = useCallback(async (id: string, name: string) => {
    try {
      // Optimistically delete (will be handled by real-time subscription)
      const result = await handleDelete(id, name);
      return result;
    } catch (err) {
      // If it fails, the real-time subscription won't delete, so no rollback needed
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir categoria' };
    }
  }, [handleDelete]);

  return {
    loading,
    error,
    createCategory: handleCreate,
    updateCategory: handleUpdate,
    deleteCategory: handleDelete,
    optimisticCreate,
    optimisticUpdate,
    optimisticDelete,
    clearError: () => setError(null),
  };
}